class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

function parseFacetPrice(str, fractionDigits) {
  const fd =
    fractionDigits !== undefined ? Number.parseInt(String(fractionDigits), 10) : NaN;
  const useFd = Number.isFinite(fd) && fd >= 0 ? fd : 2;

  if (useFd === 0) {
    const digits = String(str ?? '').replace(/\D/g, '');
    if (digits === '') return NaN;
    const n = parseInt(digits, 10);
    return Number.isFinite(n) ? n : NaN;
  }

  if (str == null || str === '') return NaN;
  let s = String(str).trim().replace(/\s/g, '');
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  if (lastComma > lastDot && lastComma !== -1) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    s = s.replace(/,/g, '');
  }
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
}

function formatFacetPrice(amount, fractionDigits) {
  const fd =
    fractionDigits !== undefined ? Number.parseInt(String(fractionDigits), 10) : NaN;
  const useFd = Number.isFinite(fd) && fd >= 0 ? fd : 2;

  if (!Number.isFinite(amount)) return '';

  if (useFd === 0) return String(Math.round(amount));

  const factor = Math.pow(10, useFd);
  const rounded = Math.round(amount * factor) / factor;
  return rounded.toFixed(useFd);
}

/** Money amount in storefront units, rounded like Shopify rounding for this currency precision */
function roundAmount(amount, fractionDigits) {
  const fd =
    fractionDigits !== undefined ? Number.parseInt(String(fractionDigits), 10) : NaN;
  const useFd = Number.isFinite(fd) && fd >= 0 ? fd : 2;
  const n = Number(amount);
  if (!Number.isFinite(n)) return 0;
  if (useFd <= 0) return Math.round(n);
  const factor = Math.pow(10, useFd);
  return Math.round(n * factor) / factor;
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.boundOnRangeSlide = this.boundOnRangeSlide.bind(this);
    this.boundOnRangeCommit = this.boundOnRangeCommit.bind(this);
    this.boundOnTextChange = this.onRangeChange.bind(this);
    this.boundOnTextInputLive = () => this.syncHandlesFromTexts();
    this.boundOnKeyDown = this.onKeyDown.bind(this);
    this.ceilingAmount = NaN;
    this.fractionDigits = 2;
    this.catalogCeiling = NaN;
    this.priceFacetRoot = null;
  }

  connectedCallback() {
    if (this.dataset.priceInit === 'true') return;
    this.dataset.priceInit = 'true';

    this.priceFacetRoot = this.querySelector('.price-facet');
    const fdStr = this.priceFacetRoot?.dataset.fractionDigits;
    const fdParsed =
      fdStr !== undefined && fdStr !== ''
        ? parseInt(String(fdStr), 10)
        : NaN;
    this.fractionDigits =
      Number.isFinite(fdParsed) && fdParsed >= 0 ? fdParsed : 2;

    const ceilingRaw =
      typeof this.priceFacetRoot?.dataset.amountCeiling === 'string'
        ? this.priceFacetRoot.dataset.amountCeiling.trim()
        : '';
    let ceilingParsed =
      ceilingRaw !== '' ? parseFacetPrice(ceilingRaw, this.fractionDigits) : NaN;
    if (!Number.isFinite(ceilingParsed)) {
      ceilingParsed = NaN;
    }

    this.minInput = this.querySelector('[data-price-input="min"]');
    this.maxInput = this.querySelector('[data-price-input="max"]');
    this.minRange = this.querySelector('[data-price-range="min"]');
    this.maxRange = this.querySelector('[data-price-range="max"]');
    this.fill = this.querySelector('.js-price-fill');

    if (!this.minInput || !this.maxInput) return;

    if (Number.isFinite(ceilingParsed) && ceilingParsed > 0) {
      this.ceilingAmount = ceilingParsed;
      this.catalogCeiling = ceilingParsed;
      const ceilingFmt = formatFacetPrice(ceilingParsed, this.fractionDigits);
      this.minInput.setAttribute('data-max', ceilingFmt);
      this.maxInput.setAttribute('data-max', ceilingFmt);
    } else if (this.maxInput.placeholder) {
      const fromPh = parseFacetPrice(this.maxInput.placeholder.trim(), this.fractionDigits);
      if (Number.isFinite(fromPh) && fromPh > 0) {
        this.ceilingAmount = fromPh;
        this.catalogCeiling = fromPh;
        const ceilingFmt = formatFacetPrice(fromPh, this.fractionDigits);
        this.minInput.setAttribute('data-max', ceilingFmt);
        this.maxInput.setAttribute('data-max', ceilingFmt);
      }
    }

    this.minInput.addEventListener('keydown', this.boundOnKeyDown);
    this.minInput.addEventListener('change', this.boundOnTextChange);
    this.minInput.addEventListener('input', this.boundOnTextInputLive);

    this.maxInput.addEventListener('keydown', this.boundOnKeyDown);
    this.maxInput.addEventListener('change', this.boundOnTextChange);
    this.maxInput.addEventListener('input', this.boundOnTextInputLive);

    this.setMinAndMaxValues();

    if (this.minRange && this.maxRange && this.fill) {
      if (!Number.isFinite(this.ceilingAmount) || this.ceilingAmount <= 0) {
        this.ceilingAmount = parseFacetPrice(this.minInput.getAttribute('data-max'), this.fractionDigits);
      }

      if (!Number.isFinite(this.ceilingAmount) || this.ceilingAmount <= 0) {
        return;
      }

      this.minRange.max = String(this.ceilingAmount);
      this.maxRange.max = String(this.ceilingAmount);
      this.minRange.step = this.fractionDigits <= 0 ? '1' : 'any';
      this.maxRange.step = this.fractionDigits <= 0 ? '1' : 'any';

      this.minRange.addEventListener('input', this.boundOnRangeSlide);
      this.maxRange.addEventListener('input', this.boundOnRangeSlide);

      this.minRange.addEventListener('change', this.boundOnRangeCommit);
      this.maxRange.addEventListener('change', this.boundOnRangeCommit);

      window.requestAnimationFrame(() => {
        this.syncHandlesFromTexts();
      });
    }
  }

  boundOnRangeSlide(event) {
    event.stopPropagation();
    this.crossConstrainHandles();
    this.syncTextsFromHandlesSilently();
    this.updateFill();
  }

  boundOnRangeCommit(event) {
    event.stopPropagation();
    this.crossConstrainHandles();
    this.syncTextsFromHandlesSilently();
    this.setMinAndMaxValues();
    this.clearPriceFilterInputsWhenWholeCatalogRange();
    const targetThumb = event.currentTarget === this.minRange ? this.minInput : this.maxInput;
    this.mergeFacetFormsSubmit(targetThumb);
  }

  mergeFacetFormsSubmit(targetHint) {
    const form = this.closest('form');
    const host = form?.closest('facet-filters-form');
    const targetInput = targetHint || this.maxInput || this.minInput;

    if (host && typeof host.onSubmitHandler === 'function') {
      host.onSubmitHandler({
        preventDefault: () => {},
        target: targetInput,
        currentTarget: form,
        srcElement: targetInput,
      });
      return;
    }

    const fireInput = (el) => {
      try {
        el.dispatchEvent(new InputEvent('input', { bubbles: true, cancelable: true, composed: true }));
      } catch {
        el.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
      }
    };

    fireInput(this.minInput);
    fireInput(this.maxInput);
  }

  /**
   * Omit price params from URL when range is "everything" (<= 0 … catalog max): same UX as Dawn empty fields.
   * @returns {boolean} Whether both inputs were cleared.
   */
  clearPriceFilterInputsWhenWholeCatalogRange() {
    const ceil = this.catalogCeiling;
    if (!Number.isFinite(ceil) || ceil <= 0) return;

    const minRaw = this.minInput.value.trim();
    const maxRaw = this.maxInput.value.trim();

    const minP =
      minRaw === '' ? 0 : parseFacetPrice(minRaw, this.fractionDigits);
    let maxP = maxRaw === '' ? ceil : parseFacetPrice(maxRaw, this.fractionDigits);

    if (!Number.isFinite(minP) || minP > 0) return false;
    if (!Number.isFinite(maxP)) return false;

    const fd = this.fractionDigits;
    const tol = fd <= 0 ? 0.5 : Math.pow(10, -(fd + 1));
    maxP = roundAmount(maxP, fd);
    const ceilR = roundAmount(ceil, fd);
    const atCeiling =
      fd <= 0 ? maxP === ceilR : Math.abs(maxP - ceilR) <= tol;

    if (atCeiling && minP <= 0) {
      this.minInput.value = '';
      this.maxInput.value = '';
      return true;
    }
    return false;
  }

  syncTextsFromHandlesSilently() {
    const ceiling = Number.isFinite(this.ceilingAmount) ? this.ceilingAmount : NaN;
    if (!Number.isFinite(ceiling)) return;

    let rm = parseFloat(this.minRange.value);
    let rx = parseFloat(this.maxRange.value);
    if (!Number.isFinite(rm)) rm = 0;
    if (!Number.isFinite(rx)) rx = 0;

    let minV = Math.min(ceiling, Math.max(0, rm));
    let maxV = Math.min(ceiling, Math.max(minV, rx));
    minV = roundAmount(minV, this.fractionDigits);
    maxV = roundAmount(maxV, this.fractionDigits);

    this.minInput.value = formatFacetPrice(minV, this.fractionDigits);
    this.maxInput.value = formatFacetPrice(maxV, this.fractionDigits);
  }

  crossConstrainHandles() {
    if (!this.minRange || !this.maxRange) return;
    let rawMin = parseFloat(this.minRange.value);
    let rawMax = parseFloat(this.maxRange.value);
    if (!Number.isFinite(rawMin)) rawMin = 0;
    if (!Number.isFinite(rawMax)) rawMax = 0;
    let minV = roundAmount(rawMin, this.fractionDigits);
    let maxV = roundAmount(rawMax, this.fractionDigits);
    const activeEl = document.activeElement;

    if (minV > maxV) {
      if (activeEl === this.minRange) {
        maxV = minV;
        this.maxRange.value = String(maxV);
      } else if (activeEl === this.maxRange) {
        minV = maxV;
        this.minRange.value = String(minV);
      } else {
        maxV = minV;
        this.maxRange.value = String(maxV);
      }
    }

    const fd = this.fractionDigits;
    this.minRange.value = fd <= 0 ? String(Math.round(minV)) : String(roundAmount(minV, fd));
    this.maxRange.value = fd <= 0 ? String(Math.round(maxV)) : String(roundAmount(maxV, fd));
  }

  syncHandlesFromTexts() {
    if (!this.minRange || !this.maxRange) return;

    const ceiling = Number.isFinite(this.ceilingAmount)
      ? this.ceilingAmount
      : parseFacetPrice(this.minInput.getAttribute('data-max'), this.fractionDigits);
    const rawMin = this.minInput.value.trim();
    const rawMax = this.maxInput.value.trim();

    let maxNum =
      rawMax === '' ? ceiling : parseFacetPrice(this.maxInput.value, this.fractionDigits);
    if (!Number.isFinite(ceiling)) return;
    if (!Number.isFinite(maxNum)) {
      maxNum = ceiling;
    }
    maxNum = Math.min(ceiling, Math.max(0, maxNum));

    let minNum = rawMin === '' ? 0 : parseFacetPrice(this.minInput.value, this.fractionDigits);
    if (!Number.isFinite(minNum)) minNum = 0;
    minNum = Math.min(ceiling, Math.max(0, Math.min(minNum, maxNum)));

    minNum = roundAmount(minNum, this.fractionDigits);
    maxNum = roundAmount(maxNum, this.fractionDigits);

    this.minRange.value = formatFacetPrice(minNum, this.fractionDigits);
    this.maxRange.value = formatFacetPrice(maxNum, this.fractionDigits);
    this.updateFill();
  }

  updateFill() {
    if (!this.fill || !this.minRange || !this.maxRange) return;
    const ceiling = Number(this.minRange.max) || Number(this.ceilingAmount);
    if (!Number.isFinite(ceiling) || ceiling <= 0) return;

    const minV = Number.parseFloat(this.minRange.value) || 0;
    const maxV = Number.parseFloat(this.maxRange.value) || 0;
    const leftPct = (minV / ceiling) * 100;
    const widthPct = Math.max(0, ((maxV - minV) / ceiling) * 100);
    this.fill.style.left = `${leftPct}%`;
    this.fill.style.width = `${widthPct}%`;
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncHandlesFromTexts();
    this.crossConstrainHandles();
    this.syncTextsFromHandlesSilently();
    this.updateFill();

    const clearedFullRange = this.clearPriceFilterInputsWhenWholeCatalogRange();
    if (clearedFullRange) {
      this.mergeFacetFormsSubmit(event.currentTarget);
    }
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    if (this.fractionDigits <= 0) {
      const pattern =
        /[0-9]| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
      if (!event.key.match(pattern)) event.preventDefault();
      return;
    }

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.querySelector('[data-price-input="min"]');
    const maxInput = this.querySelector('[data-price-input="max"]');
    if (!minInput || !maxInput) return;
    const catalogCeilingStr = Number.isFinite(this.catalogCeiling)
      ? formatFacetPrice(this.catalogCeiling, this.fractionDigits)
      : maxInput.getAttribute('data-max');

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '' && catalogCeilingStr) minInput.setAttribute('data-max', catalogCeilingStr);
  }

  adjustToValidValues(input) {
    const raw = input.value.trim();
    if (raw === '') return;

    const value = parseFacetPrice(input.value, this.fractionDigits);
    if (!Number.isFinite(value)) return;

    let minBound = parseFacetPrice(input.getAttribute('data-min'), this.fractionDigits);
    let maxBound = parseFacetPrice(input.getAttribute('data-max'), this.fractionDigits);

    if (!Number.isFinite(minBound)) minBound = 0;
    if (!Number.isFinite(maxBound)) return;

    if (value < minBound) input.value = formatFacetPrice(minBound, this.fractionDigits);
    else if (value > maxBound) input.value = formatFacetPrice(maxBound, this.fractionDigits);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
